import {
  Button,
  Card,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { ReactElement } from "react";

import { DeleteOptOutListResult } from "~frontend/__generated__/graphql";

import { FlexBox } from "../FlexBox";
import { LightDivider } from "../TwoColumnDisplay";

const OTHER_SUBACCOUNT = "OtherSubaccount";

const otherSubaccountSuffix = "by another Subaccount in your organization";

const optOutTypeMapping: {
  [key: string]: {
    copy: string;
    singularVerb?: string;
    pluralVerb?: string;
  };
} = {
  optedOutGlobally: {
    copy: "opted out globally by your organization",
  },
  optedOutAutomatically: {
    copy: "also automatically opted out",
  },
  optedOutByTexter: {
    copy: "opted out by a texter",
  },
  optedOutInList: {
    copy: "opted out in other Opt Out Lists",
  },
  optedOutTemporarily: {
    copy: "in Strike Lists",
    singularVerb: "appears",
    pluralVerb: "appear",
  },
  optedOutInSpoke: {
    copy: "opted out via Spoke sync",
  },
  optedOutInSpokeGlobally: {
    copy: "opted out via Spoke sync globally by your organization",
  },
  optedOutShortCode: {
    copy: "opted out via short code response",
  },
};

export const DeletedOptOutListDialog = ({
  listName,
  deletionSummary,
  onClose,
}: {
  listName: string;
  deletionSummary: DeleteOptOutListResult;
  onClose: () => void;
}): ReactElement => {
  const { __typename, textableCount, listCount, ...otherCounts } =
    deletionSummary;

  return (
    <Dialog open>
      <DialogTitle>Deleted {listName}</DialogTitle>
      <DialogContent>
        <Card>
          <CardHeader
            sx={{
              paddingX: 2,
              paddingY: 1,
            }}
            title={
              <Typography variant="body2">
                <strong>Deletion Summary</strong>
              </Typography>
            }
          />

          <FlexBox direction="column" gap={1} sx={{ paddingX: 2, paddingY: 3 }}>
            <Typography variant="body1">
              {listCount} Opt Outs from {listName}
            </Typography>
            <FlexBox direction="column" gap={1} sx={{ paddingX: 3 }}>
              {Object.keys(otherCounts).map(key => {
                const keyPrefix = key.replace(
                  OTHER_SUBACCOUNT,
                  ""
                ) as keyof typeof optOutTypeMapping;

                const count =
                  otherCounts[
                    key as keyof Omit<
                      DeleteOptOutListResult,
                      "textableCount" | "listCount" | "id" | "__typename"
                    >
                  ] || 0;

                if (count === 0) {
                  return null;
                }

                const defaultVerb = count > 1 ? "are" : "is";
                const verb =
                  count > 1
                    ? optOutTypeMapping[keyPrefix].pluralVerb
                    : optOutTypeMapping[keyPrefix].singularVerb;
                const copy = optOutTypeMapping[keyPrefix].copy;

                return (
                  <Typography key={key} variant="body2">
                    {count} contact{count > 1 && "s"} {verb ?? defaultVerb}{" "}
                    {copy}{" "}
                    {key.includes(OTHER_SUBACCOUNT) && otherSubaccountSuffix}
                  </Typography>
                );
              })}
            </FlexBox>
            <LightDivider />
            <Typography>
              <strong>
                {textableCount} contact{textableCount > 1 && "s"} from this list{" "}
                {textableCount === 1 ? "is" : "are"} now textable
              </strong>
            </Typography>
          </FlexBox>
        </Card>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
